import AsyncStorage from '@react-native-async-storage/async-storage';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

export type OnboardingPersona =
  | 'organization_manager'
  | 'team_manager'
  | 'parent'
  | 'player';

type OnboardingState = {
  persona: OnboardingPersona | null;
  organizationId: string | null;
  organizationTitle: string | null;
  seasonId: string | null;
  seasonTitle: string | null;
  teamIds: string[];
  scheduleStartDate: string | null;
  scheduleSlots: Array<{
    id: string;
    datetime: string;
    location: string | null;
  }>;
  matchupCount: number;
  repeatEveryWeeks: number;
  lastRoute: string | null;
  setPersona(persona: OnboardingPersona): void;
  setOrganizationId(organizationId: string | null): void;
  setOrganizationTitle(organizationTitle: string | null): void;
  setSeasonId(seasonId: string | null): void;
  setSeasonTitle(seasonTitle: string | null): void;
  setTeamIds(teamIds: string[]): void;
  setScheduleStartDate(scheduleStartDate: string | null): void;
  setScheduleSlots(
    scheduleSlots: Array<{
      id: string;
      datetime: string;
      location: string | null;
    }>,
  ): void;
  setScheduleOptions(matchupCount: number, repeatEveryWeeks: number): void;
  setLastRoute(lastRoute: string | null): void;
  reset(): void;
};

const initialState = {
  persona: null,
  organizationId: null,
  organizationTitle: null,
  seasonId: null,
  seasonTitle: null,
  teamIds: [],
  scheduleStartDate: null,
  scheduleSlots: [],
  matchupCount: 1,
  repeatEveryWeeks: 1,
  lastRoute: null,
};

const useOnboardingStore = createWithEqualityFn<OnboardingState>()(
  persist(
    (set) => ({
      ...initialState,
      setPersona: (persona) => set(() => ({ persona })),
      setOrganizationId: (organizationId) => set(() => ({ organizationId })),
      setOrganizationTitle: (organizationTitle) =>
        set(() => ({ organizationTitle })),
      setSeasonId: (seasonId) => set(() => ({ seasonId })),
      setSeasonTitle: (seasonTitle) => set(() => ({ seasonTitle })),
      setTeamIds: (teamIds) => set(() => ({ teamIds })),
      setScheduleStartDate: (scheduleStartDate) =>
        set(() => ({ scheduleStartDate })),
      setScheduleSlots: (scheduleSlots) => set(() => ({ scheduleSlots })),
      setScheduleOptions: (matchupCount, repeatEveryWeeks) =>
        set(() => ({ matchupCount, repeatEveryWeeks })),
      setLastRoute: (lastRoute) => set(() => ({ lastRoute })),
      reset: () => set(() => initialState),
    }),
    {
      name: 'useOnboardingStore',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        persona: state.persona,
        organizationId: state.organizationId,
        organizationTitle: state.organizationTitle,
        seasonId: state.seasonId,
        seasonTitle: state.seasonTitle,
        teamIds: state.teamIds,
        scheduleStartDate: state.scheduleStartDate,
        scheduleSlots: state.scheduleSlots,
        matchupCount: state.matchupCount,
        repeatEveryWeeks: state.repeatEveryWeeks,
        lastRoute: state.lastRoute,
      }),
    },
  ),
);

export default useOnboardingStore;
